import { capitalize } from "lodash-es"
export const getDisplayName = (text: string): string => {
  return capitalize(text.replace(/_/g, " "))
}

// Define score levels based on score ranges
export const SCORE_LEVELS = [
  {
    value: "exact",
    label: "Exact",
    minScore: 0.99,
    maxScore: 1.0,
    color: "#05AA20",
    backgroundColor: "#F4FFF8",
  },
  {
    value: "great",
    label: "Great",
    minScore: 0.8,
    maxScore: 0.99,
    color: "#05AA20",
    backgroundColor: "#F4FFF8",
  },
  {
    value: "good",
    label: "Good",
    minScore: 0.6,
    maxScore: 0.8,
    color: "var(--leaf-green)",
    backgroundColor: "#F4FFF8",
  },
  {
    value: "fair",
    label: "Fair",
    minScore: 0.4,
    maxScore: 0.6,
    color: "var(--canary-yellow)",
    backgroundColor: "#FFFAE6",
  },
  {
    value: "limited",
    label: "Limited",
    minScore: 0.0,
    maxScore: 0.4,
    color: "var(--alert-dark)",
    backgroundColor: "#FFF0F0",
  },
] as const

export type ScoreLevel = (typeof SCORE_LEVELS)[number]["value"]
export type ScoreLevelObject = (typeof SCORE_LEVELS)[number]

export const getScore = (score: number): ScoreLevelObject => {
  const level = SCORE_LEVELS.find(
    (scoreLevel) =>
      score >= scoreLevel.minScore &&
      (scoreLevel.maxScore === 1.0
        ? score <= scoreLevel.maxScore
        : score < scoreLevel.maxScore),
  )
  return level ?? undefined
}

export const getScoreFormatted = (score: number): string => {
  return `${(score * 100).toFixed(0)}%`
}

/**
 * Checks if a score falls within any of the selected score levels
 */
export const isScoreInSelectedLevels = (
  score: number,
  selectedScoreLevels: ScoreLevel[],
): boolean => {
  return selectedScoreLevels.some((level) => {
    const scoreLevel = SCORE_LEVELS.find((sl) => sl.value === level)
    if (!scoreLevel) return false
    return score >= scoreLevel.minScore && score < scoreLevel.maxScore
  })
}

// Types for filtering
type SmartMatchingData = {
  people: ReadonlyArray<{
    readonly personId: number
    readonly totalScore: number
    [key: string]: any // Allow additional properties
  }>
  [key: string]: any // Allow additional properties like embeddingFields
}

/**
 * Filters people based on smart matching scores and selected score levels
 */
export const filterPeopleBySmartMatchingScores = <T extends { id: number }>(
  people: readonly T[],
  smartMatchingData: SmartMatchingData | null,
  selectedScoreLevels: ScoreLevel[],
) => {
  if (!smartMatchingData) {
    return people
  }

  // Create a map of person IDs to their scores
  const personScores = new Map(
    smartMatchingData.people.map((person) => [
      person.personId,
      person.totalScore,
    ]),
  )

  // Filter to only include people who have scores in the selected ranges
  return people.filter((person) => {
    const score = personScores.get(person.id)
    if (score === undefined) {
      return false // Don't show people without scores
    }

    // Check if the score falls within any of the selected score levels
    return isScoreInSelectedLevels(score, selectedScoreLevels)
  })
}

/**
 * Sorts people by smart match score first (descending), then by availability for ties
 */
export const sortPeopleByScoreAndAvailability = <T extends { id: number }>(
  people: readonly T[],
  smartMatchingData: SmartMatchingData | null,
  availabilitySortedIds: number[],
): T[] => {
  // Create a map of person IDs to their scores
  const personScores = new Map(
    smartMatchingData?.people.map((person) => [
      person.personId,
      person.totalScore,
    ]) ?? [],
  )

  return [...people].sort((a, b) => {
    const scoreA = personScores.get(a.id) ?? 0
    const scoreB = personScores.get(b.id) ?? 0

    // Sort by score first (descending - higher scores first)
    if (scoreA !== scoreB) {
      return scoreB - scoreA
    }

    // For ties, sort by availability
    return (
      availabilitySortedIds.indexOf(a.id) - availabilitySortedIds.indexOf(b.id)
    )
  })
}
